<template>
  <div class="row card">
    <!-- Header -->
    <div class="align-items-center">
      <div class="border-0 mb-4">
        <div
          class="card-header py-3 no-bg bg-transparent d-flex align-items-center px-0 justify-content-between border-bottom flex-wrap"
        >
          <h3 class="fw-bold mb-0">Report</h3>
        </div>
      </div>
    </div>

    <div class="row overflow-auto">
      <div class="col-sm-12">
        <!-- Search Bar -->
        <div class="row">
          <div class="w-100 col-sm-12">
            <div id="patient-table_filter" class="dataTables_filter">
              <div class="row">

                <!-- Date From -->
                <div class="col">
                  <label class="form-label">From Date</label>
                  <br />
                  <input
                    type="date"
                    v-model="request.fromDate"
                    id="date"
                    class="form-control"
                  />
                </div>

                <!-- Date To -->
                <div class="col">
                  <label class="form-label">To Date</label>
                  <br />
                  <input
                    type="date"
                    v-model="request.toDate"
                    id="date"
                    class="form-control"
                  />
                </div>

                <!-- Status -->
                <div class="col">
                  <label class="form-label">Status</label>
                  <select v-model="request.status" class="form-select">
                    <option :value="null">--</option>
                    <option
                      v-for="opt in statusOptions"
                      :key="opt.numericMeasure"
                      :value="opt.numericMeasure"
                    >
                      {{ opt.name }}
                    </option>
                  </select>
                </div>

                <!-- CompanyId -->
                <div class="col">
                  <label class="form-label">Company</label>
                  <select v-model.number="request.companyId" class="form-select">
                    <option :value="null">--</option>
                    <option v-for="c in companies" :key="c.id" :value="c.id">
                      {{ c.name }}
                    </option>
                  </select>
                </div>

                <!-- Email -->
                <div class="col">
                  <label class="form-label">Email</label>
                  <input
                    type="text"
                    v-model.trim="request.email"
                    class="form-control"
                    placeholder="Email"
                  />
                </div>

                <!-- Buttons -->
                <div class="col d-flex align-items-end">
                  <button @click.prevent="search(true)" class="btn btn-primary">
                    <i class="icofont-ui-search"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <br />
        <div class="row">
          <div class="col-12 mb-3">
            <div class="card p-3">
              <div class="d-flex align-items-center justify-content-between">
                <div>
                  <div class="fw-bold">Game Analytics</div>
                  <div class="text-muted small">
                    Total played: {{ analytics.totalGamePlayed ?? 0 }}
                  </div>
                  <div class="text-muted small">
                    Success: {{ analytics.successfulPlayersCount ?? 0 }}
                  </div>
                  <div class="text-muted small">
                    Failed: {{ analytics.failedPlayersCount ?? 0 }}
                  </div>
                </div>
                <div class="pieWrap">
                  <apexchart
                    v-if="pie.total > 0"
                    type="donut"
                    height="210"
                    :options="donutOptions"
                    :series="donutSeries"
                  />
                  <div v-if="pie.total > 0" class="pieLegend mt-2">
                    <span class="legendItem">
                      <span class="dot dotSuccess"></span>
                      Success: {{ pie.success }}
                    </span>
                    <span class="legendItem ms-3">
                      <span class="dot dotFail"></span>
                      Failed: {{ pie.fail }}
                    </span>
                  </div>
                  <div v-else class="text-muted small text-end">
                    No data
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="col-12">
            <div class="d-flex align-items-center justify-content-between flex-wrap mb-2">
              <div><b>Total - </b>{{ totalCount }}</div>
              <div class="d-flex align-items-center">
                <label class="me-2 mb-0">Page size</label>
                <select
                  v-model.number="request.pageSize"
                  class="form-select form-select-sm w-auto"
                  @change="search(true)"
                >
                  <option :value="10">10</option>
                  <option :value="25">25</option>
                  <option :value="50">50</option>
                </select>
              </div>
            </div>

            <!-- Table -->
            <table
              id="patient-table"
              class="table table-striped table-bordered align-middle mb-0 nowrap dataTable no-footer dtr-inline"
              style="width: 100%"
              role="grid"
              aria-describedby="patient-table_info"
            >
              <thead>
                <tr role="row">
                  <th class="text-center">Status</th>
                  <th class="text-center">Email</th>
                  <th class="text-center">Company</th>
                  <th class="text-center">Start Date</th>
                  <th class="text-center">End Date</th>
                  <th class="text-center">Attempts</th>
                  <th class="text-center">Duration</th>
                </tr>
              </thead>

              <tbody>
                <tr v-for="(row, idx) in rows" :key="idx" class="text-center">
                  <td>
                    <span
                      class="badge"
                      :class="getStatusMeta(row.status).badgeClass"
                    >
                      {{ getStatusMeta(row.status).label }}
                    </span>
                  </td>
                  <td>{{ row.email }}</td>
                  <td>{{ row.company }}</td>
                  <td>{{ format_date(row.startDate) }}</td>
                  <td>{{ format_date(row.endDate) }}</td>
                  <td>{{ row.attempts }}</td>
                  <td>{{ format_duration(row.duration) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <br />
    <pagination
      :totalPages="totalPage"
      :perPage="request.pageSize"
      :currentPage="currentPage"
      @pagechanged="onPageChange"
    />
  </div>
</template>

<script>
import jwtInterceptor from "../../jwt.interceptor";
import Pagination from "../parts/site/Pagination.vue";
import moment from "moment";

export default {
  name: "Report",
  components: {
    Pagination,
  },
  data() {
    return {
      rows: [],
      analytics: {},
      statusOptions: [],
      companies: [],
      totalCount: 0,
      request: {
        fromDate: "",
        toDate: "",
        pageIndex: 1,
        pageSize: 10,
        status: null,
        companyId: null,
        email: "",
      },
      currentPage: 1,
      totalPage: 0,
    };
  },
  computed: {
    pie() {
      const success = Number(this.analytics?.successfulPlayersCount ?? 0);
      const fail = Number(this.analytics?.failedPlayersCount ?? 0);
      const total = success + fail;
      const successPct = total > 0 ? Math.round((success / total) * 100) : 0;
      const failPct = total > 0 ? 100 - successPct : 0;
      return { total, success, fail, successPct, failPct };
    },
    donutSeries() {
      return [this.pie.success, this.pie.fail];
    },
    donutOptions() {
      return {
        chart: {
          type: "donut",
          animations: { enabled: true },
          sparkline: { enabled: true },
          fontFamily: "inherit",
        },
        labels: ["Success", "Failed"],
        colors: ["#198754", "#dc3545"],
        stroke: { width: 0 },
        legend: { show: false },
        dataLabels: { enabled: false },
        states: {
          hover: { filter: { type: "none" } },
          active: { filter: { type: "none" } },
        },
        tooltip: {
          y: {
            formatter: (val) => `${val}`,
          },
        },
        plotOptions: {
          pie: {
            donut: {
              size: "75%",
              labels: {
                show: true,
                name: {
                  show: true,
                  offsetY: 14,
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#6c757d",
                },
                value: {
                  show: true,
                  offsetY: -10,
                  fontSize: "20px",
                  fontWeight: 700,
                  color: "#212529",
                  formatter: () => `${this.pie.successPct}%`,
                },
                total: {
                  show: true,
                  label: "Success rate",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#6c757d",
                  formatter: () => `${this.pie.successPct}%`,
                },
              },
            },
          },
        },
        responsive: [
          {
            breakpoint: 768,
            options: { chart: { width: 180 } },
          },
        ],
      };
    },
  },
  methods: {
    fetchCompanies() {
      jwtInterceptor
        .get(this.$store.getters.getUrl + "/Companies")
        .then((response) => {
          this.companies = Array.isArray(response.data) ? response.data : [];
        })
        .catch((error) => {
          console.log(error);
          this.companies = [];
        });
    },
    fetchStatusOptions() {
      jwtInterceptor
        .get(this.$store.getters.getUrl + "/ChoiceValues")
        .then((response) => {
          const list = Array.isArray(response.data) ? response.data : [];
          this.statusOptions = list
            .filter(
              (x) =>
                x &&
                typeof x.numericMeasure === "number" &&
                typeof x.name === "string"
            )
            .sort((a, b) => a.numericMeasure - b.numericMeasure);
        })
        .catch((error) => {
          console.log(error);
          this.statusOptions = [];
        });
    },
    getStatusMeta(statusValue) {
      const found = this.statusOptions.find(
        (x) => x.numericMeasure === statusValue
      );
      const label = found?.name ?? `${statusValue ?? ""}`;

      // Default palette for known values; falls back to secondary.
      const v = Number(statusValue);
      const badgeClass =
        v === 1 ? "bg-success" : v === 2 ? "bg-danger" : "bg-secondary";

      return { label, badgeClass };
    },
    onPageChange(page) {
      this.request.pageIndex = page;
      this.currentPage = page;
      this.search(false);
    },
    search(resetPage = false) {
      if (resetPage) {
        this.request.pageIndex = 1;
        this.currentPage = 1;
      }
      this.fetchAnalytics();
      this.fetchReport();
    },
    fetchAnalytics() {
      jwtInterceptor
        .get(this.$store.getters.getUrl + "/GameResult/GameAnalytics", {
          params: {
            FromDate: this.request.fromDate || undefined,
            ToDate: this.request.toDate || undefined,
          },
        })
        .then((response) => {
          this.analytics = response.data ?? {};
        })
        .catch((error) => {
          console.log(error);
          this.analytics = {};
          this.$toast.error("Analytics alınmadı!", { position: "top-right" });
        });
    },
    fetchReport() {
      jwtInterceptor
        .get(this.$store.getters.getUrl + "/GameResult/Report", {
          params: {
            FromDate: this.request.fromDate || undefined,
            ToDate: this.request.toDate || undefined,
            PageIndex: this.request.pageIndex,
            PageSize: this.request.pageSize,
            Status: this.request.status ?? undefined,
            CompanyId: this.request.companyId ?? undefined,
            Email: this.request.email || undefined,
          },
        })
        .then((response) => {
          const body = response.data ?? {};
          this.totalCount = body.totalCount ?? 0;
          this.totalPage = body.totalPages ?? 0;
          this.rows = body.data ?? [];
        })
        .catch((error) => {
          console.log(error);
          this.rows = [];
          this.totalCount = 0;
          this.totalPage = 0;
          this.$toast.error("Report alınmadı!", { position: "top-right" });
        });
    },
    format_date(k) {
      return k ? moment(k).format("DD.MM.Y HH:mm:ss") : "";
    },
    format_duration(v) {
      if (!v) return "";
      const s = String(v);
      // Expected: "HH:MM:SS.fffffff" (or without fractional part)
      const main = s.split(".")[0];
      const parts = main.split(":");
      if (parts.length < 3) return main;

      const [hh, mm, ss] = parts;
      return `${hh}:${mm}:${ss}`;
    },
  },
  created() {
    this.fetchStatusOptions();
    this.fetchCompanies();
    this.search(true);
  },
};
</script>

<style>
[data-theme="high-contrast"] {
  .table-striped > tbody > tr:nth-of-type(odd) > * {
    color: white !important;
  }
}

.pieWrap {
  min-width: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.pieLegend {
  width: 100%;
  display: flex;
  justify-content: center;
  font-size: 0.85rem;
  color: #495057;
}

.legendItem {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  display: inline-block;
  margin-right: 6px;
}
.dotSuccess {
  background: #198754;
}
.dotFail {
  background: #dc3545;
}
</style>
